import { itemNameGroupView } from "../query/item-group-presentation.js";
import { contestQueryTermView } from "../fishing-contest/query-presentation.js";
import { buildAppliedSearchTermsView } from "../components/applied-search-terms.js";
import { fishIdentityMarkup, resolveFishGrade } from "../components/fish-identity.js";
import { zoneIdentityMarkup } from "../components/zone-identity.js";
import { titleIdentityMarkup } from "./title-presentation.js";
import { fishQueryTermRecords } from "./query.js";
import { tokenizeSearchExpressionDsl } from "../query/lex.js";
import { layoutQueryRibbon } from "../query/ribbon-layout.js";
import { createQueryScrollController } from "../query/scroll-controller.js";
import { escapeHtml, queryIconMarkup, QUERY_FILTER_ICONS, queryMeaningCopy, queryStatusMarkup, queryStatusIndicator, renderQueryStatus } from "../query/presentation.js";

function fishRecordIdentity(record) {
  const entry = record.entry;
  return { ...entry, fishId: record.id, itemId: record.id, isPrize: entry.is_prize, iconImage: entry.icon_image, encyclopediaIconImage: entry.encyclopedia_icon_image };
}
function fishNameGroupView(record, t) {
  return itemNameGroupView(record.members.map(fishRecordIdentity), t);
}
export function fishQueryRecordMarkup(record, field = record.kind, t) {
  if (record.kind === "fish-group") return fishNameGroupView(record, t).contentMarkup;
  const entry = record.entry;
  if (record.kind === "fish") return fishIdentityMarkup(fishRecordIdentity(record));
  if (record.kind === "title") return titleIdentityMarkup(entry);
  const [r, g, b] = entry.rgb.split(",").map(Number);
  return `<span class="inline-flex items-center gap-1.5">${queryIconMarkup(QUERY_FILTER_ICONS[field].icon)}${zoneIdentityMarkup({ r, g, b, zoneRgb: (r << 16) | (g << 8) | b, name: entry.name })}</span>`;
}
export function fishQueryFilterMarkup(term, label) {
  const { icon, iconClass } = QUERY_FILTER_ICONS[term] || { icon: "nav-dex", iconClass: "" };
  return `<span class="inline-flex items-center gap-1.5">${queryIconMarkup(icon, iconClass)}<span>${escapeHtml(label)}</span></span>`;
}
export function fishQueryExpressionView(parsed, source, index, t, contestFish) {
  const tokens = tokenizeSearchExpressionDsl(source);
  function visit(node, path = "root") {
    if (!node) return null;
    const binding = { sourceStart: node.sourceStart, sourceEnd: node.sourceEnd, sourceText: node.sourceText, path, key: path, negated: node.negated };
    if (node.kind === "group") return {
      ...binding, type: "group", operator: node.operator,
      children: node.children.map((child,i) => visit(child, `${path}.${i}`)),
      operatorSources: node.children.slice(1).map((child,i) => {
        const start = node.children[i].sourceEnd, end = child.sourceStart;
        const operator = tokens.find(token => token.start >= start && token.end <= end && token.value.toLowerCase() === node.operator);
        return { sourceStart: operator?.start ?? start, sourceEnd: operator?.end ?? end, sourceText: source.slice(operator?.start ?? start, operator?.end ?? end), sourceImplicit: !operator };
      }),
    };
    if (node.field === "contest") return { ...contestQueryTermView(contestFish, t), ...binding };
    let contentMarkup, grade = node.field === "grade" ? node.value : "", label = node.value;
    if (["text", "fish", "title", "zone", "potential-zone"].includes(node.field)) {
      const records = fishQueryTermRecords(node, index);
      if (records.length === 1) {
        contentMarkup = fishQueryRecordMarkup(records[0], node.field, t); label = records[0].label;
        if (records[0].kind === "fish-group") {
          ({ label, grade, contentMarkup } = fishNameGroupView(records[0], t));
        }
        if (records[0].kind === "fish") grade = resolveFishGrade({ ...records[0].entry, isPrize: records[0].entry.is_prize });
      } else {
        // A broad term keeps its literal meaning; never portray one match as the entire filter.
        contentMarkup = fishQueryFilterMarkup(node.field, label);
      }
    } else { label = t(`fishydex.query.term.${node.value}`); contentMarkup = fishQueryFilterMarkup(node.value, label); }
    if (node.field === "potential-zone") contentMarkup += `<span class="badge badge-soft badge-xs">${escapeHtml(t("fishydex.query.field.potential-zone"))}</span>`;
    return { ...binding, type: "term", label, contentMarkup, grade, kindLabel: node.field === "text" ? "" : t(`fishydex.query.field.${node.field}`) };
  }
  return visit(parsed.expression);
}

// Owns only native draft presentation and the acknowledged catalog projection.
export function mountFishQueryPresentation({ host, status, input, t }) {
  const surface = host.querySelector('.fishymap-query-surface');
  const editor = host.querySelector('.fishymap-query-editor');
  const selection = host.querySelector('.fishymap-query-selection');
  const shell = host.querySelector('.fishymap-query-selection-shell');
  const measure = host.querySelector('.fishymap-query-source-measure');
  status.innerHTML = queryStatusMarkup("fishydex-query-status-detail");
  let applied = null, frame = null, disposed = false, composing = false;
  let ribbonKey = "";
  function scheduleLayout() {
    if (disposed || frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      const rail = selection.querySelector('.fishy-applied-query-rail');
      if (rail) layoutQueryRibbon(rail);
      if (input.ownerDocument.activeElement === input) scrolling.revealCaretEdge();
    });
  }
  const scrolling = createQueryScrollController({ host: shell, scrollElement: editor, input, onCaretEdgeRevealRequested: scheduleLayout });
  const resize = new ResizeObserver(scheduleLayout); resize.observe(editor);
  const lifecycle = new AbortController();
  host.addEventListener('load', scheduleLayout, { capture: true, signal: lifecycle.signal });
  input.ownerDocument.fonts?.addEventListener('loadingdone', scheduleLayout, { signal: lifecycle.signal });
  function paint(state, detail, relation) {
    measure.textContent = input.value || " ";
    renderQueryStatus(status, surface, { state, relation, label: t(`map.query.status_indicator.${queryStatusIndicator(state)}`), detail });
    const view = applied?.expression;
    const result = buildAppliedSearchTermsView(view, { escapeHtml, sourceText: applied?.source || "", queryTextRelation: relation, meaningCopy: queryMeaningCopy(t) });
    if (ribbonKey !== result.renderKey) { selection.innerHTML = result.html; ribbonKey = result.renderKey; }
    shell.hidden = selection.hidden = !result.hasContent;
    input.setAttribute('aria-invalid', String(state === "invalid"));
    scheduleLayout();
  }
  return {
    pending(isComposing = false) {
      composing = isComposing;
      if (!composing && applied && input.value.trim() === applied.draftSource.trim()) {
        paint(applied.state, applied.detail, applied.source ? applied.state === "partial" ? "valid-prefix" : "current-text" : "none");
        return;
      }
      paint(composing ? "composing" : "evaluating", t(`fishydex.query.${composing ? "composing" : "updating"}`), applied?.source ? "prior-query" : "none");
    },
    get appliedSource() { return applied?.source || ""; },
    acknowledge({ state, source, draftSource, parsed, index, contestFish, message = "" }) {
      const expression = source && applied?.source === source && applied?.parsed === parsed && applied?.index === index && applied?.contestFish === contestFish
        ? applied.expression : source ? fishQueryExpressionView(parsed, source, index, t, contestFish) : null;
      applied = { source, draftSource, state, parsed, index, contestFish, expression, detail: message || t(`fishydex.query.state.${state}`) };
      const current = input.value.trim() === source.trim();
      const relation = source ? current ? "current-text" : state === "partial" ? "valid-prefix" : "prior-query" : "none";
      const visibleState = composing ? "composing" : state === "applied" && !current ? "evaluating" : state;
      const detail = composing ? t("fishydex.query.composing") : visibleState === "evaluating" ? t("fishydex.query.updating") : message || t(`fishydex.query.state.${state}`);
      paint(visibleState, detail, relation);
    },
    dispose() { disposed = true; lifecycle.abort(); resize.disconnect(); scrolling.dispose(); if (frame !== null) cancelAnimationFrame(frame); },
  };
}
